import {
  Accordion,
  Box,
  Flex,
  FormLabel,
  Input,
  Link,
  ListItem,
  Text,
  UnorderedList,
} from "@chakra-ui/react";
import { FormattedMessage, useIntl } from "react-intl";

import { RanaButton } from "@nens/ranalib";

import { RanaAccordionItem } from "@/components/rana-accordion-item";

import { CopyButton } from "../../../_internal";
import { PublicationDetailShareCreateForm } from "../../share/share-create-or-edit";
import { useFilesAsUrlsExplanation } from "../hooks/use-files-as-urls-explanation";

export function FilesAsUrlsExplanation() {
  const intl = useIntl();

  const { createdShareKey, setCreatedShareKey, showCreateForm, setShowCreateForm } =
    useFilesAsUrlsExplanation();

  const createApiKeyString = intl.formatMessage({
    id: "publication.file_as_url.create_api_key",
    defaultMessage: "Create API key",
  });

  return (
    <Accordion allowToggle mb={4} defaultIndex={0}>
      <RanaAccordionItem
        title={intl.formatMessage({
          id: "publication.file_as_url.basic_auth.title",
          defaultMessage: "How Authentication works",
        })}
      >
        <Flex gap={4} justify="space-between" align="flex-start">
          <Box w="full">
            <Text mb={2}>
              <FormattedMessage
                id="publication.file_as_url.basic_auth.intro_before_link"
                defaultMessage="Publication API keys are used to authenticate external applications using {basicAuthLink}:"
                values={{
                  basicAuthLink: (
                    <Link
                      href="https://en.wikipedia.org/wiki/Basic_access_authentication"
                      isExternal
                      color="brand.clear_creek"
                    >
                      basic authentication
                    </Link>
                  ),
                }}
              />
            </Text>

            <UnorderedList mb={3} ml={6} spacing={1}>
              <ListItem>
                <FormattedMessage
                  id="publication.file_as_url.basic_auth.username_rule"
                  defaultMessage='Username: {username} (including the double underscores before and after the word "key")'
                  values={{
                    username: (
                      <Text as="span" fontFamily="mono">
                        __key__
                      </Text>
                    ),
                  }}
                />
              </ListItem>
              <ListItem>
                <FormattedMessage
                  id="publication.file_as_url.basic_auth.password_rule"
                  defaultMessage="Password: {password}"
                  values={{
                    password: (
                      <Text as="span" fontFamily="mono">
                        <FormattedMessage
                          id="publication.file_as_url.basic_auth.password_value"
                          defaultMessage="Your API key"
                        />
                      </Text>
                    ),
                  }}
                />
              </ListItem>
            </UnorderedList>

            <FormLabel mb={2}>
              <FormattedMessage
                id="publication.file_as_url.basic_auth.example_title"
                defaultMessage="For example"
              />
            </FormLabel>
            <Box
              bg="brand.light_sky"
              px={3}
              py={2}
              borderRadius="base"
              fontFamily="mono"
              whiteSpace="pre-wrap"
              mb={3}
            >
              <FormattedMessage
                id="publication.file_as_url.basic_auth.example_block"
                defaultMessage={"username: __key__\npassword: example.apikey"}
              />
            </Box>

            <Text>
              <FormattedMessage
                id="publication.file_as_url.basic_auth.visible_once"
                defaultMessage="An API key is only visible when it is created. For security reasons, it cannot be viewed again afterward."
              />
            </Text>
          </Box>

          <Flex direction="column" justifyContent="space-between" gap={3} w="full">
            {createdShareKey ? (
              <Box w="fit" ml="auto">
                <FormLabel>
                  <FormattedMessage
                    id="publication.file_as_url.public_api_key"
                    defaultMessage="Publication API key"
                  />
                </FormLabel>
                <Flex w="fit" ml="auto">
                  <Input
                    onFocus={(event) => event.target.select()}
                    isReadOnly
                    value={createdShareKey}
                    aria-label={intl.formatMessage({
                      defaultMessage: "Publication API key",
                      id: "publication.file_as_url.public_api_key",
                    })}
                    title={intl.formatMessage({
                      id: "publication.file_as_url.public_api_key",
                      defaultMessage: "Publication API key",
                    })}
                  />
                  <CopyButton
                    copyText={createdShareKey}
                    title={intl.formatMessage({
                      id: "publication.file_as_url.copy_api_key",
                      defaultMessage: "Copy API key",
                    })}
                    toastMessage={intl.formatMessage({
                      id: "success.copied_api_key",
                      defaultMessage: "Copied API key to clipboard.",
                    })}
                  />
                </Flex>
              </Box>
            ) : (
              <RanaButton ml="auto" variant="primary" onClick={() => setShowCreateForm(true)}>
                {createApiKeyString}
              </RanaButton>
            )}
          </Flex>
        </Flex>
      </RanaAccordionItem>

      {showCreateForm && (
        <PublicationDetailShareCreateForm
          onClose={() => setShowCreateForm(false)}
          setShareKey={(shareKey) => {
            setCreatedShareKey(shareKey);
            setShowCreateForm(false);
          }}
          title={createApiKeyString}
        />
      )}
    </Accordion>
  );
}
